import { Button, Flex, ModalV2, Text, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import {
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
} from "~/graphql/supergraphEnums";
import {
  useOnboardingCaseForSubjectQuery,
  useUnlinkOnboardingCaseMutation,
} from "~/graphql/supergraphHooks";
import { MAIN_SERVICE } from "~/pages/Compliance/utils/constants";
import { hasUnlinkSubjectIds } from "./hasUnlinkSubjectIds";

export type OnboardingSubjectKind = "entity" | "client";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  subjectKind: OnboardingSubjectKind;
  subjectId: number;
  subjectName: string;
  arenaId: number;
  onUnlinked: () => void;
};

const CASE_TYPE_BY_SUBJECT: Record<OnboardingSubjectKind, ComplianceCaseTypeEnum> = {
  entity: ComplianceCaseTypeEnum.EntityOnboarding,
  client: ComplianceCaseTypeEnum.ClientOnboarding,
};

const OBJECT_TYPE_BY_SUBJECT: Record<OnboardingSubjectKind, ComplianceExternalObjectType> = {
  entity: ComplianceExternalObjectType.Account,
  client: ComplianceExternalObjectType.Arena,
};

export const OnboardingUnlinkModal = ({
  isOpen,
  onClose,
  subjectKind,
  subjectId,
  subjectName,
  arenaId,
  onUnlinked,
}: Props) => {
  const canResolve = hasUnlinkSubjectIds(subjectId, arenaId);
  const caseType = CASE_TYPE_BY_SUBJECT[subjectKind];

  const { data, loading: resolvingCase } = useOnboardingCaseForSubjectQuery({
    variables: {
      caseType,
      externalObject: {
        objectId: String(subjectId),
        objectTypeEnum: OBJECT_TYPE_BY_SUBJECT[subjectKind],
        service: MAIN_SERVICE,
        name: subjectName,
      },
    },
    skip: !isOpen || !canResolve || caseType == null,
    fetchPolicy: "no-cache",
  });
  const caseId = data?.onboardingCaseForSubject?.id ?? null;

  const [unlinkOnboardingCase, { loading: unlinking }] = useUnlinkOnboardingCaseMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success(`Case unlinked from ${subjectName}`);
      onUnlinked();
      onClose();
    },
  });

  const handleUnlink = async () => {
    if (caseId == null || !canResolve) {
      return;
    }
    await unlinkOnboardingCase({ variables: { caseId, arenaId } });
  };

  if (!canResolve) {
    return null;
  }

  return (
    <ModalV2 show={isOpen} onHide={onClose} title="Unlink case">
      <ModalV2.Body>
        <Text>Are you sure you want to unlink the onboarding case from {subjectName}?</Text>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex flexDirection="row" gap={Sizes.SM} justifyContent="flex-end">
          <Button variant="neutral" onClick={onClose} disabled={unlinking}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleUnlink}
            isLoading={unlinking}
            disabled={resolvingCase || caseId == null}
            data-testid="confirm-unlink-onboarding-case"
          >
            Unlink
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
